
import { Mixins, Component } from "vue-property-decorator";
import { Getter } from "vuex-class";
import PaginatedSearch from '@/mixins/PaginatedSearch';
import { PaginatedSearchQueryParams } from '@/store/types';
import { Label, LabelTypesForDisplay, LabelTypes, LabelType } from '@/store/labels/types';
import GenericTable, { TableHeader } from "@/components/table/generic-table.vue";
import GenericUpdateModal, { ModalItem } from '@/components/table/generic-update-modal.vue';
import { UserPermissions, FeaturesList } from "@/store/types";
import AreaTreeSelect from "@/components/form/AreaTreeSelect.vue";
import labelApi from '@/services/api.labelSetup.service';
import { debounce, get } from "lodash";
import { getDebouncePeriod } from '@/utils/debounce-helper';

@Component({
	components:{
		"generic-table": GenericTable,
		"generic-update-modal": GenericUpdateModal,
		"area-tree-select": AreaTreeSelect
	},
})

export default class LabelSetup extends Mixins(PaginatedSearch) {
	@Getter("getPermissions") permissions: UserPermissions;
	@Getter("getFeaturesList") private featuresList: FeaturesList;

	private isLoading: boolean = false;
	private totalLabels: number = 0;
	private paginatedSearchQueryParams: PaginatedSearchQueryParams;
	private labels: Label[] = [];

	private async mounted(): Promise<void> {
		try
		{
			await this.retrieveLabels();
		} catch (e) {
			console.error("Failed to get Labels ", e);
		}
	}

	private get canViewLabels(): boolean {
		return !!this.permissions.canViewLabels;
	}

	private get canEditLabels(): boolean {
		return !!this.permissions.canEditLabels;
	}

	private get labellingActionPlanSetupFeatureEnabled(): boolean {
		return get(this.featuresList, ["Labelling", "ActionPlanSetup"], false);
	}

	private async retrieveLabels(): Promise<void> {
		try {
			if (this.canViewLabels) {
				this.isLoading = true;

				let request = this.generateNewPaginatedSearchRequest(this.paginatedSearchQueryParams);
				request.cancelToken = null;
				request.params.live = true;

				let labelApiResult = await labelApi.getLabels(request);
				this.labels = labelApiResult.data;
				this.totalLabels = labelApiResult.totalRecords;
			}
		}
		catch (ex) {
			console.error("Unexpected error fetching labels: " + ex);
		}
		finally {
			this.isLoading = false;
		}
	}

	private async updatePaginationParameters(paginatedSearchQueryParams?: PaginatedSearchQueryParams): Promise<void> {
		this.paginatedSearchQueryParams = paginatedSearchQueryParams;
		await this.retrieveLabels();
	}

	private async deleteLabel(label: Label): Promise<void> {
		try {
			if (this.canViewLabels && this.canEditLabels) {
				this.isLoading = true;
				await labelApi.deleteLabel(label.labelId);
			}
		}
		catch (ex) {
			console.error("Failed to delete label: " + ex);
		}
		finally {
			this.isLoading = false;
			await this.retrieveLabels();
		}
	}

	private saveLabelDebounce = debounce(async (label:Label) => {
		await this.saveLabel(label);
	}, getDebouncePeriod());

	private async saveLabel(label: Label): Promise<void> {
		try {
			if (this.canViewLabels && this.canEditLabels) {
				this.isLoading = true;
				await labelApi.createOrUpdateLabel(label);
				await this.retrieveLabels();
			}
		}
		catch (ex) {
			console.error("Failed to save label: " + ex);
		}
		finally {
			this.isLoading = false;
		}
	}

	private get labelTypeOptions(): LabelType[] {
		if (!this.labellingActionPlanSetupFeatureEnabled) {
			return LabelTypesForDisplay.filter(type =>
				type.labelTypeId !== LabelTypes.ActionPlan
			);
		}

		return LabelTypesForDisplay;
	}

	private get columns(): TableHeader[] {
		return [
			{
				title: "Title",
				key: "title",
				order: 0,
				sortOrder: 0,
				sortOrderReversed: false,
				description: "Label title",
				visible: true,
				dataType: "string",
				isTermLabel: true,
				sortable: true,
				isSortedByDefault: true,
				searchable: true,
			},
			{
				title: "Area",
				key: "groupTitle",
				order: 1,
				sortOrder: 1,
				sortOrderReversed: false,
				description: "The Area the Label belongs to",
				visible: true,
				dataType: "input",
				isTermLabel: false,
				sortable: true
			},
			{
				title: "Label Type",
				key: "labelTypeTitle",
				order: 2,
				sortOrder: 2,
				sortOrderReversed: false,
				description: "Label type",
				visible: true,
				dataType: "string",
				isTermLabel: true,
				sortable: true,
			},
		]
	}

	private get modalItems(): ModalItem[] {
		return [
			{
				title: "Title",
				key: "title",
				dataType: "string",
				required: true,
				maxLength: 50,
			},
			{
				title: "Area",
				key: "groupId",
				dataType: "component",
				data: AreaTreeSelect,
				required: true,
				props: {
					reduce: (option) => option.id,
					appendToBody: true,
				},
			},
			{
				title: "Label Type",
				key: "labelTypeId",
				dataType: "vselect3",
				data: this.labelTypeOptions,
				props: {
					reduce: (option) => option.labelTypeId,
					appendToBody: true,
				},
				required: true,
			},
		]
	}
}
